import { useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { copyTextToClipboard } from 'components/Dashboard/helpers/copyFunctionality';
import { useHeightContainer } from 'components/Dashboard/utilities/useHeightContainer';
import { useCreatePrompt } from './Create';
import isJsonString from 'components/Dashboard/helpers/isJsonString';
import { useTypeOfUsers } from 'components/Dashboard/SidebarElements/parliamentarianUsers';
import TransformFromPascalCase from 'components/Settings/Utilities/TransformFromPascalCase';
import normalize from 'components/Dashboard/utilities/normalizeString';
import useIsMobile from 'components/Dashboard/utilities/useIsMobile';
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton';
import CustomScrollbar from 'components/Common/CustomScrollbar';
import ModalComponent from 'components/Common/Modal';
import ReactMarkdown from 'react-markdown';
import createMarkup from 'components/Dashboard/helpers/createMarkup';
import { useSuggestionFunctions } from 'components/WritingAssistant/WritingCreate';

const Content = (props) => {
  const { results, isLoading, updateFunction, deleteItem } = props;
  const { body, contentType, promptData } = results;

  const [heightContainer, containerRef] = useHeightContainer();
  const { getResponseToConstituent } = useSuggestionFunctions();
  const [heightContainerMessage, containerRefMessage] = useHeightContainer();
  const [showDeletePopup, setShowDeletePopup] = useState(false);
  const [isLoadingRegenerate, setIsLoadingRegenerate] = useState(false);

  const { createPrompt } = useCreatePrompt();

  const changeContentType = () => {
    switch (contentType) {
      case 'ParliamentarianDebateContribution':
        return 'Debate contribution';
      case 'ParliamentarianEDM':
        return 'Early Day Motion';
      case 'ParliamentarianConsituentResponse':
        return 'Suggested response';
      case 'ParliamentarianQuestion':
        return 'Question';
      default:
        return TransformFromPascalCase(contentType);
    }
  };

  const adjustContentTypeForPrompt = () => {
    switch (contentType) {
      case 'ParliamentarianDebateContribution':
        return 'debate-contribution';
      case 'ParliamentarianEDM':
        return 'edm';
      case 'ParliamentarianConsituentResponse':
        return 'response-to-a-constituent';
      case 'ParliamentarianQuestion':
        return 'question';
      default:
        return normalize(TransformFromPascalCase(contentType));
    }
  };

  const textContainer = useRef(null);

  const { isParliamentarian } = useTypeOfUsers();
  const isMobile = useIsMobile();
  const DeleteButton = () => {
    return (
      <button
        className='general-button border-general border-red-60 hovered-button hovered-button-red px-4 py-1 py-lg-2 mr-lg-3 text-red rounded-pill'
        onClick={() => {
          setShowDeletePopup(true);
        }}
      >
        Delete
      </button>
    );
  };

  return (
    <>
      <div className='flex-grow-1'>
        <div className='bg-light-red disclaimer-shadow  writing-assistant-bar user-select-none'>
          <div className='mx-auto main-content-wrapper px-lg-5 px-3 d-flex py-4 '>
            <Link to={{ pathname: '/ai-assistant' }} className='main-dark-text mr-4 reset-link writing-back-link'>
              <span className='icon-long-arrow-left' />
            </Link>
            <div>
              <h4 className='title-h5-bold'>Disclaimer</h4>
              <p className='d-flex align-items-center mb-0'>
                The text below was written by PolicyMogul’s AI assistant. This feature is in Beta and output may contain
                factual errors. You can use it verbatim, but we suggest you read it carefully and consider making edits
                to ensure it is accurate and reflects your views.
              </p>
            </div>
          </div>
        </div>
        <div className='d-flex h-100 main-content-wrapper  mx-auto pt-3 pt-lg-4 px-lg-5 px-3 gap-25'>
          <div
            className='box-top-rounded col-lg-4 px-0 d-none d-lg-block'
            ref={containerRefMessage}
            style={{ height: `${heightContainerMessage}px` }}
          >
            <CustomScrollbar className={'simple-scrollbar'} maximalThumbYSize={100}>
              <div className='py-3 specific-x-padding-16 specific-x-padding-lg-32 py-lg-4 ai-assistant-message'>
                <h3 className='title-h5-m-bold flex-centered mb-3 specific-y-padding-lg-8 user-select-none'>
                  Message from constituent
                </h3>
                {isLoading ? (
                  <>
                    {Array(4)
                      .fill()
                      .map((item, index) => (
                        <div key={index} className='mb-3'>
                          <Skeleton count={3} height={19} width={`100%`} />
                          <Skeleton height={19} width={`45%`} />
                        </div>
                      ))}
                  </>
                ) : (
                  <>
                    <div dangerouslySetInnerHTML={createMarkup(promptData?.title)} />
                    {promptData?.phrases &&
                      (() => {
                        try {
                          const parsedPhrases = JSON.parse(promptData.phrases);
                          return parsedPhrases[0] ? (
                            <div className='mt-3'>
                              <h3 className='title-h5-m-bold flex-centered mb-3 specific-y-padding-lg-8'>
                                Instructions for this response
                              </h3>
                              <div dangerouslySetInnerHTML={createMarkup(parsedPhrases[0])} />
                            </div>
                          ) : null;
                        } catch (e) {
                          return null;
                        }
                      })()}
                  </>
                )}
              </div>
            </CustomScrollbar>
          </div>
          <div className='mx-auto flex-grow-1 d-flex flex-column'>
            <div className='box-top-rounded h-100'>
              <div ref={containerRef} style={{ height: `${heightContainer}px` }}>
                <CustomScrollbar className={'simple-scrollbar'} maximalThumbYSize={100}>
                  <div className='specific-x-padding-24 specific-x-padding-lg-32 py-4'>
                    {isLoading ? (
                      <ContentSkeleton />
                    ) : (
                      <>
                        <div className='d-lg-flex align-items-center justify-content-between mb-3 user-select-none'>
                          <div className='flex-centered justify-content-between mb-4 mb-lg-0'>
                            <h3 className='title-h5-m-bold flex-centered mb-0'>
                              <span className='icon-bot title-h2 inline-block mr-2' />
                              {changeContentType()}
                            </h3>
                            {isMobile && <DeleteButton />}
                          </div>
                          <div className='flex-centered my-content-buttons'>
                            {!isMobile && <DeleteButton />}
                            <button
                              className='text-light-blue border-general border-light-blue hovered-button general-button px-3 py-1 px-lg-4 py-lg-2 mr-2 mr-lg-3 rounded-pill'
                              disabled={isLoadingRegenerate}
                              onClick={async () => {
                                setIsLoadingRegenerate(true);
                                let prompt = createPrompt({
                                  contentType: adjustContentTypeForPrompt(),
                                  title: results?.promptData?.title,
                                  phrases: !!results?.promptData?.phrases
                                    ? JSON.parse(results?.promptData?.phrases)
                                    : [],
                                });
                                let suggestion = await getResponseToConstituent({ prompt });
                                await updateFunction({ prompt, body: JSON.stringify(suggestion) });
                                setIsLoadingRegenerate(false);
                              }}
                            >
                              {isLoadingRegenerate && (
                                <i className='fas fa-spinner fa-spin mr-2 d-inline-block-centered' />
                              )}{' '}
                              Regenerate
                            </button>
                            <button
                              className={`general-button rounded-pill ${
                                isParliamentarian ? 'action-button' : 'outline-active-button'
                              }  px-3 py-1 px-lg-4 py-lg-2`}
                              onClick={() => {
                                copyTextToClipboard(textContainer?.current?.innerText);
                              }}
                            >
                              Copy {isMobile ? '' : 'text'}
                            </button>
                          </div>
                        </div>
                        <div className='pl-lg-2'>
                          <div className='assistant-text news-content-in' ref={textContainer}>
                            <ReactMarkdown
                              components={{
                                a: ({ href, children, node, ...props }) => {
                                  return (
                                    <a
                                      href={href}
                                      className='general-link text-light-blue'
                                      target='_blank'
                                      rel='noopener noreferrer'
                                      {...props}
                                    >
                                      {children}
                                    </a>
                                  );
                                },
                                p: ({ children, node, ...props }) => {
                                  return (
                                    <p className='mb-3' {...props}>
                                      {children}
                                    </p>
                                  );
                                },
                              }}
                            >
                              {isJsonString(body)
                                ? (() => {
                                    const parsed = JSON.parse(body);
                                    if (Array.isArray(parsed?.output)) {
                                      return parsed.output.map((output) => output?.content[0]?.text ?? '').join('\n');
                                    }
                                    if (Array.isArray(parsed?.choices)) {
                                      parsed?.choices?.map((item, index) => {
                                        return <p key={`content-${index}`}>{item?.message?.content ?? item?.text}</p>;
                                      });
                                    }
                                    return '';
                                  })()
                                : body}
                            </ReactMarkdown>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                </CustomScrollbar>
              </div>
            </div>
          </div>
        </div>
        <DeleteItemPopup setShowPopup={setShowDeletePopup} showPopUp={showDeletePopup} deleteItem={deleteItem} />
      </div>
    </>
  );
};

const ContentSkeleton = () => {
  return (
    <SkeletonTheme baseColor='#f2f2f2'>
      <div className='flex-centered justify-content-between mb-5'>
        <Skeleton height={24} width={`400px`} />
        <div className='flex-centered'>
          <div className='mr-3'>
            <Skeleton height={24} width={`120px`} />
          </div>
          <Skeleton height={24} width={`120px`} />
        </div>
      </div>
      <div className='pl-5'>
        {Array(4)
          .fill()
          .map((item, index) => (
            <div key={index} className='mb-3'>
              <Skeleton count={3} height={19} width={`100%`} />
              <Skeleton height={19} width={`45%`} />
            </div>
          ))}
      </div>
    </SkeletonTheme>
  );
};

const DeleteItemContent = (props) => {
  const navigate = useNavigate();
  const { deleteItem, setShowPopup } = props;
  return (
    <div className='p-3 px-lg-5 pt-lg-5 pb-lg-2 delete-keywordcategory-modal'>
      <div className='text-center'>
        <p>Are you sure you want to delete this item?</p>

        <div className='my-4'>
          <button
            className='general-button danger-button px-4 py-2'
            onClick={async (e) => {
              let el = e.target;
              el.disabled = true;
              let deleteContent = await deleteItem();
              if (deleteContent) {
                setShowPopup(false);
                setTimeout(() => {
                  navigate('/ai-assistant');
                }, 300);
              }
            }}
          >
            {`Delete item`}
          </button>
        </div>
      </div>
    </div>
  );
};

const DeleteItemPopup = (props) => {
  const { showPopUp, setShowPopup } = props;
  return (
    <ModalComponent isOpen={showPopUp} setIsOpen={setShowPopup} maxWidth={500}>
      <DeleteItemContent {...props} />
    </ModalComponent>
  );
};

export { ContentSkeleton };
export default Content;
